"use client";

import * as React from "react";
import dynamic from "next/dynamic";

import PhotowallTvFrame from "@/components/photowall-landing/PhotowallTvFrame";

// The wall's own black 16:9 screen, shown until the wall and its code load,
// so nothing shifts when it mounts.
const SCREEN_CLASS_NAME = "aspect-video w-full bg-black";

// The wall's code (the arrival engine, the QR code) loads only when it mounts.
const PhotowallLoopingWall = dynamic(
  () => import("@/components/photowall-landing/PhotowallLoopingWall"),
  { ssr: false, loading: () => <div className={SCREEN_CLASS_NAME} /> },
);

interface HomeLiveWallProps {
  label: string;
}

/**
 * The photo wall page's demo wall: a burst of guest photos a second after it
 * shows, then one now and then. The homepage and the event pages use it far
 * down the page, so it mounts only near the viewport: the wall warms all of
 * its demo photos as soon as it mounts.
 */
export default function HomeLiveWall({
  label,
}: HomeLiveWallProps): React.JSX.Element {
  const placeholderRef = React.useRef<HTMLDivElement | null>(null);
  const [shouldMount, setShouldMount] = React.useState(false);

  React.useEffect(() => {
    const element = placeholderRef.current;
    if (!element || shouldMount) return;
    if (typeof IntersectionObserver === "undefined") {
      setShouldMount(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldMount(true);
        observer.disconnect();
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldMount]);

  return (
    <div className="relative">
      <PhotowallTvFrame>
        {shouldMount ? (
          <PhotowallLoopingWall label={label} uploads="showcase" />
        ) : (
          <div
            ref={placeholderRef}
            role="img"
            aria-label={label}
            className={SCREEN_CLASS_NAME}
          />
        )}
      </PhotowallTvFrame>
      <span
        aria-hidden="true"
        className="absolute left-[clamp(14px,2vw,24px)] top-[clamp(14px,2vw,24px)] z-20 flex h-3 w-3"
      >
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75 motion-reduce:animate-none" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500 ring-2 ring-white/80" />
      </span>
    </div>
  );
}
